import React, { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Plus, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";

const PICKLISTS = [
  { field: "booking_type", label: "Type of booking" },
  { field: "villa", label: "Villa allocated" },
  { field: "addon_experience", label: "Add-on experiences" },
  { field: "status", label: "Status" },
];

const FIELD_TYPES = [
  { v: "text", l: "Text" }, { v: "textarea", l: "Long text" }, { v: "number", l: "Number" },
  { v: "date", l: "Date" }, { v: "select", l: "Dropdown" }, { v: "multiselect", l: "Multi-select" },
  { v: "boolean", l: "Yes / No" },
];

const PicklistManager = ({ field, label, options, reload }) => {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!val.trim()) return;
    setBusy(true);
    try {
      await api.post("/bookings/options", { field, label: val.trim() });
      setVal("");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); } finally { setBusy(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/bookings/options/${id}`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  return (
    <div className="border border-zinc-200 rounded-md p-3">
      <div className="text-sm font-semibold mb-2">{label}</div>
      <div className="flex flex-wrap gap-2 mb-2">
        {(options || []).length === 0 && <span className="text-xs text-zinc-400">No options yet.</span>}
        {(options || []).map((o) => (
          <span key={o.id} className="inline-flex items-center gap-1 text-xs bg-zinc-100 rounded-full pl-3 pr-1.5 py-1" data-testid={`opt-${field}-${o.key}`}>
            {o.label}
            {o.is_system ? <Lock className="w-3 h-3 text-zinc-400" /> : (
              <button onClick={() => del(o.id)} className="hover:text-red-600" data-testid={`opt-del-${field}-${o.key}`}><Trash2 className="w-3 h-3" /></button>
            )}
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder={`Add ${label.toLowerCase()}…`} className="h-9" data-testid={`opt-input-${field}`} />
        <Button size="sm" onClick={add} disabled={busy} data-testid={`opt-add-${field}`}><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

export const BookingConfigDialog = ({ open, onOpenChange, config, reload }) => {
  const options = config?.options || {};
  const fields = config?.fields || [];
  const [nf, setNf] = useState({ label: "", type: "text", required: false, options: "" });
  const [busy, setBusy] = useState(false);

  const addField = async () => {
    if (!nf.label.trim()) return toast.error("Field name is required");
    const needsOpts = nf.type === "select" || nf.type === "multiselect";
    const optList = nf.options.split(",").map((s) => s.trim()).filter(Boolean);
    if (needsOpts && optList.length === 0) return toast.error("Add comma-separated options for a dropdown");
    setBusy(true);
    try {
      await api.post("/bookings/fields", { label: nf.label.trim(), type: nf.type, required: nf.required, options: needsOpts ? optList : null });
      setNf({ label: "", type: "text", required: false, options: "" });
      reload();
      toast.success("Field added");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add field"); } finally { setBusy(false); }
  };
  const delField = async (id) => {
    try { await api.delete(`/bookings/fields/${id}`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete field"); }
  };

  const needsOpts = nf.type === "select" || nf.type === "multiselect";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="booking-config-dialog">
        <DialogHeader>
          <DialogTitle>Configure booking form</DialogTitle>
          <DialogDescription>Manage picklist options and add custom fields. These apply to every booking form.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[68vh] overflow-y-auto pr-1">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2">Picklists</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PICKLISTS.map((p) => (
                <PicklistManager key={p.field} field={p.field} label={p.label} options={options[p.field]} reload={reload} />
              ))}
            </div>
          </div>

          <div className="pt-3 border-t border-zinc-100">
            <div className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2">Custom fields</div>
            <div className="space-y-2 mb-3">
              {fields.length === 0 && <div className="text-xs text-zinc-400">No custom fields yet.</div>}
              {fields.map((fd) => (
                <div key={fd.id} className="flex items-center justify-between gap-2 border border-zinc-200 rounded-md px-3 py-2" data-testid={`field-row-${fd.key}`}>
                  <div className="text-sm">
                    <span className="font-medium">{fd.label}</span>
                    <span className="ml-2 text-[10px] uppercase tracking-widest bg-zinc-100 rounded px-1.5 py-0.5">{fd.type}</span>
                    {fd.required && <span className="ml-1 text-[10px] uppercase tracking-widest text-amber-700">required</span>}
                    {(fd.options || []).length > 0 && <span className="ml-2 text-xs text-zinc-500">{fd.options.map((o) => o.label).join(", ")}</span>}
                  </div>
                  <button onClick={() => delField(fd.id)} className="text-zinc-400 hover:text-red-600" data-testid={`field-del-${fd.key}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>

            <div className="border border-dashed border-zinc-300 rounded-md p-3 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-zinc-500">Field name</Label>
                  <Input value={nf.label} onChange={(e) => setNf((s) => ({ ...s, label: e.target.value }))} className="mt-1 h-9" placeholder="e.g. Arrival flight no." data-testid="new-field-label" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Field type</Label>
                  <Select value={nf.type} onValueChange={(v) => setNf((s) => ({ ...s, type: v }))}>
                    <SelectTrigger className="mt-1 h-9" data-testid="new-field-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {needsOpts && (
                <div>
                  <Label className="text-xs text-zinc-500">Options (comma-separated)</Label>
                  <Input value={nf.options} onChange={(e) => setNf((s) => ({ ...s, options: e.target.value }))} className="mt-1 h-9" placeholder="Option A, Option B, Option C" data-testid="new-field-options" />
                </div>
              )}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={nf.required} onCheckedChange={(c) => setNf((s) => ({ ...s, required: !!c }))} data-testid="new-field-required" /> Required
                </label>
                <Button size="sm" onClick={addField} disabled={busy} data-testid="new-field-add">
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Add field
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
